import React from 'react';
import { useRouter } from 'next/router';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { ApplicationRejected } from '@components/modals/ArtistApplication/ApplicationRejected';
import { ApplicationSubmittedModal } from '@components/modals/ArtistApplication/ApplicationSubmitted';
import { ModalContext, ModalType } from '@context/modal';
import { ArtistApplicationStatus } from '@graphql/reactQuery';
import { useAuthArtistApplicationStatus } from '@hooks/useAuthArtistApplicationStatus';
import { trackClickEvent } from '@lib/analytics';
import { NAV_LINKS } from '@utils/constants';
import { NewNavLink } from '../styles';

export const ForArtists = ({
  navLoading,
  closeNav,
}: {
  navLoading?: boolean;
  closeNav: () => void;
}) => {
  const { pathname } = useRouter();
  const { status, hasApplied, loading: artistAppStatusPending } = useAuthArtistApplicationStatus();
  const { openModal } = ModalContext.useContainer();

  const isArtistApplicationPage = pathname === NAV_LINKS.ArtistApplication;

  if (status === ArtistApplicationStatus.Accepted) {
    return null;
  }

  const onForArtistsClick = () => {
    if (!hasApplied && !artistAppStatusPending) return;
    trackClickEvent({
      event: 'Join as an Artist Clicked',
      properties: {
        sourceComponent: 'Navigation',
      },
    });

    if (isArtistApplicationPage) {
      return closeNav();
    }
    if (status === ArtistApplicationStatus.Pending) {
      trackClickEvent({
        event: 'Artist Application Submitted Modal Opened',
        properties: {
          sourceComponent: 'Navigation',
        },
      });
      return openModal(ModalType.ARTIST_APPLICATION_SUBMITTED, {
        body: <ApplicationSubmittedModal nav />,
      });
    }

    if (status === ArtistApplicationStatus.Rejected) {
      trackClickEvent({
        event: 'Artist Application Rejected Modal Opened',
        properties: {
          sourceComponent: 'Navigation',
        },
      });
      return openModal(ModalType.ARTIST_APPLICATION_REJECTED, {
        body: <ApplicationRejected closeNav={closeNav} />,
      });
    }
  };

  const forArtists = hasApplied ? (
    <button onClick={onForArtistsClick}>
      <NewNavLink>For Artists</NewNavLink>
    </button>
  ) : (
    <AccessibleLink pathnameUri={NAV_LINKS.ArtistApplication} onClick={closeNav}>
      <NewNavLink>For Artists</NewNavLink>
    </AccessibleLink>
  );

  return artistAppStatusPending || navLoading ? (
    <div className="ml-4 block h-8 w-full animate-pulse rounded-sm bg-neutral200 md:h-6 md:w-36" />
  ) : (
    forArtists
  );
};
